import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Box } from '@atlaskit/primitives/compiled/box';

import Image from '@atlaskit/image/image';
import { cssMap } from '@atlaskit/css';

import type { GettingStartedCardType } from '../../PersonalizedTemplatesV2Types.ts';

const styles = cssMap({
	imageStyles: {
		width: '100%',
		objectFit: 'cover',
		// Prevent layout shift during image loads and transitions
		aspectRatio: '240 / 132',
		display: 'block',
		contain: 'layout style paint',
	},
	imageContainer: {
		// Reserve space and prevent layout thrashing
		width: '100%',
		aspectRatio: '240 / 132',
		contain: 'content',
		overflow: 'hidden',
	},
});

// Memoized Image component to prevent unnecessary re-renders during transitions
const MemoizedCardImage = React.memo(({ src, srcDark }: { src: string; srcDark: string }) => (
	<Box xcss={styles.imageContainer}>
		<Image
			src={src}
			srcDark={srcDark}
			alt=""
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
			css={styles.imageStyles}
		/>
	</Box>
));

MemoizedCardImage.displayName = 'CardImage';

const createImageComponent = (src: string, srcDark: string) => {
	return {
		component: <MemoizedCardImage src={src} srcDark={srcDark} />,
	};
};

/**
 * CDN URLs for all images
 * Provides reliable SSR rendering with browser caching benefits
 */
export const CDN_IMAGE_URLS = {
	pageImage: 'https://dam-cdn.atl.orangelogic.com/AssetLink/d0x4rv1mqv3pvoma7b0r3w66hutj4s4f.svg',
	pageImageDark:
		'https://dam-cdn.atl.orangelogic.com/AssetLink/60a3avoj78460elv2g254c15h356spe4.svg',
	brainstormWhiteboard:
		'https://dam-cdn.atl.orangelogic.com/AssetLink/gsy48bdsie2x0jl4bwwqa251ow5vc65s.svg',
	brainstormWhiteboardDark:
		'https://dam-cdn.atl.orangelogic.com/AssetLink/5j4oiocdk42kkk6567oh3j21u1ar2et3.svg',
	aiTeammate: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1608esypf8aw3u06t0i235yp27p45utr.svg',
	aiTeammateDark:
		'https://dam-cdn.atl.orangelogic.com/AssetLink/0l17d633654t1u8c8171o0xh48cbs375.svg',
	addProfilePicture:
		'https://dam-cdn.atl.orangelogic.com/AssetLink/385i88hx73177r8l53036061ca7wquy4.svg',
	addProfilePictureDark:
		'https://dam-cdn.atl.orangelogic.com/AssetLink/20pg03c85k301om1xm75652h821e55gx.svg',
	followPeopleAndSpaces:
		'https://dam-cdn.atl.orangelogic.com/AssetLink/5iljf6lua6c42o33qf34l0nt23r7078o.svg',
	followPeopleAndSpacesDark:
		'https://dam-cdn.atl.orangelogic.com/AssetLink/vn1217d2v8yw2lq888gp0b84e7u5g3tu.svg',
};

// Use CDN URLs for all images - SSR friendly and cacheable
const IMAGES = {
	pageImage: createImageComponent(CDN_IMAGE_URLS.pageImage, CDN_IMAGE_URLS.pageImageDark),
	brainstormWhiteboard: createImageComponent(
		CDN_IMAGE_URLS.brainstormWhiteboard,
		CDN_IMAGE_URLS.brainstormWhiteboardDark,
	),
	aiTeammate: createImageComponent(CDN_IMAGE_URLS.aiTeammate, CDN_IMAGE_URLS.aiTeammateDark),
	addProfilePicture: createImageComponent(
		CDN_IMAGE_URLS.addProfilePicture,
		CDN_IMAGE_URLS.addProfilePictureDark,
	),
	followPeopleAndSpaces: createImageComponent(
		CDN_IMAGE_URLS.followPeopleAndSpaces,
		CDN_IMAGE_URLS.followPeopleAndSpacesDark,
	),
};

const i18n = defineMessages({
	createPage: {
		id: 'experiment-personalized-templates-v2.nth-user.create.page',
		defaultMessage: 'Create a page',
		description:
			'Heading on the "Create a page" onboarding action card. This card appears in the nth-user onboarding cards panel to guide new users in creating their first page.',
	},
	createPageDescription: {
		id: 'experiment-personalized-templates-v2.nth-user.create.page.description',
		defaultMessage: 'Plan projects, take notes, and track tasks.',
		description:
			'Descriptive subtitle for the "Create a page" onboarding action card explaining the benefits users can achieve by creating pages.',
	},
	createPageCTA: {
		id: 'experiment-personalized-templates-v2.nth-user.create.page.cta',
		defaultMessage: 'Create page',
		description:
			'Text on the call-to-action button for the "Create a page" onboarding card. When clicked, it initiates the page creation flow.',
	},
	brainstormOnWhiteboard: {
		id: 'experiment-personalized-templates-v2.nth-user.brainstorm.on.whiteboard',
		defaultMessage: 'Brainstorm on a whiteboard',
		description:
			'Heading on the "Brainstorm on a whiteboard" onboarding action card. This card guides new users in creating whiteboards for visual collaboration.',
	},
	brainstormOnWhiteboardDescription: {
		id: 'experiment-personalized-templates-v2.nth-user.brainstorm.on.whiteboard.description',
		defaultMessage: 'Dream up new ideas and visualize your work.',
		description:
			'Descriptive subtitle for the "Brainstorm on a whiteboard" onboarding card explaining the benefits of using whiteboards for visual collaboration.',
	},
	brainstormOnWhiteboardCTA: {
		id: 'experiment-personalized-templates-v2.nth-user.brainstorm.on.whiteboard.cta',
		defaultMessage: 'Create whiteboard',
		description:
			'Text on the call-to-action button for the "Brainstorm on a whiteboard" onboarding card. When clicked, it initiates the whiteboard creation flow.',
	},
	followPeopleAndSpaces: {
		id: 'experiment-personalized-templates-v2.nth-user.follow.people.and.spaces',
		defaultMessage: 'Follow people and spaces',
		description:
			'Heading on the "Follow people and spaces" onboarding action card. This card guides new users in customizing their feed to stay updated.',
	},
	followPeopleAndSpacesDescription: {
		id: 'experiment-personalized-templates-v2.nth-user.follow.people.and.spaces.description',
		defaultMessage: 'Customize your following feed to stay up to date with what matters to you.',
		description:
			'Descriptive subtitle for the "Follow people and spaces" onboarding card explaining the benefit of customizing the feed.',
	},
	followPeopleAndSpacesCTA: {
		id: 'experiment-personalized-templates-v2.nth-user.follow.people.and.spaces.cta',
		defaultMessage: 'Customize feed',
		description:
			'Text on the call-to-action button for the "Follow people and spaces" onboarding card. When clicked, it opens the feed customization interface.',
	},
	meetAITeammate: {
		id: 'experiment-personalized-templates-v2.nth-user.meet.your.ai.teammate',
		defaultMessage: 'Meet your AI teammate',
		description:
			'Heading on the "Meet your AI teammate" onboarding action card. This card introduces Rovo to new users.',
	},
	meetAITeammateDescription: {
		id: 'experiment-personalized-templates-v2.nth-user.meet.your.ai.teammate.description',
		defaultMessage:
			'Rovo connects your knowledge and streamlines work, so you can focus on what matters.',
		description:
			'Descriptive subtitle for the "Meet your AI teammate" onboarding card explaining what Rovo (AI assistant) does and its benefits.',
	},
	meetAITeammateCTA: {
		id: 'experiment-personalized-templates-v2.nth-user.meet.your.ai.teammate.cta',
		defaultMessage: 'Chat with Rovo',
		description:
			'Text on the call-to-action button for the "Meet your AI teammate" onboarding card. When clicked, it opens the Rovo chat interface.',
	},
	addProfilePicture: {
		id: 'experiment-personalized-templates-v2.nth-user.add.profile.picture',
		defaultMessage: 'Add your profile photo',
		description:
			'Heading on the "Add your profile photo" onboarding action card. This card guides new users in uploading their profile picture.',
	},
	addProfilePictureDescription: {
		id: 'experiment-personalized-templates-v2.nth-user.add.profile.picture.description',
		defaultMessage: 'Welcome your teammates to Confluence with a familiar face.',
		description:
			'Descriptive subtitle for the "Add your profile photo" onboarding card explaining the benefit of having a profile picture for team connection.',
	},
	addProfilePictureCTA: {
		id: 'experiment-personalized-templates-v2.nth-user.add.profile.picture.cta',
		defaultMessage: 'Add photo',
		description:
			'Text on the call-to-action button for the "Add your profile photo" onboarding card. When clicked, it opens the profile photo upload interface.',
	},
	completedCTAText: {
		id: 'experiment-personalized-templates-v2.nth-user.completed.cta',
		defaultMessage: 'Completed',
		description:
			'Text displayed on the button of an onboarding action card after the user has successfully completed the associated action.',
	},
	dismissLabel: {
		id: 'experiment-personalized-templates-v2.nth-user.dismiss.label',
		defaultMessage: 'Dismiss',
		description:
			'Accessible label for the dismiss icon button on individual onboarding action cards. Used by screen readers to announce the button purpose.',
	},
});

// Onboarding actions data
export const NTH_USER_ONBOARDING_ACTIONS_DATA: GettingStartedCardType[] = [
	{
		id: 'createPage',
		image: IMAGES.pageImage,
		heading: <FormattedMessage {...i18n.createPage} />,
		description: <FormattedMessage {...i18n.createPageDescription} />,
		ctaText: <FormattedMessage {...i18n.createPageCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: () => {}, // This will be handled by the card's onDismiss prop
	},
	{
		id: 'brainstormOnWhiteboard',
		image: IMAGES.brainstormWhiteboard,
		heading: <FormattedMessage {...i18n.brainstormOnWhiteboard} />,
		description: <FormattedMessage {...i18n.brainstormOnWhiteboardDescription} />,
		ctaText: <FormattedMessage {...i18n.brainstormOnWhiteboardCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: () => {},
	},
	{
		id: 'followPeopleAndSpaces',
		image: IMAGES.followPeopleAndSpaces,
		heading: <FormattedMessage {...i18n.followPeopleAndSpaces} />,
		description: <FormattedMessage {...i18n.followPeopleAndSpacesDescription} />,
		ctaText: <FormattedMessage {...i18n.followPeopleAndSpacesCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: () => {},
	},
	{
		id: 'meetAITeammate',
		image: IMAGES.aiTeammate,
		heading: <FormattedMessage {...i18n.meetAITeammate} />,
		description: <FormattedMessage {...i18n.meetAITeammateDescription} />,
		ctaText: <FormattedMessage {...i18n.meetAITeammateCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: () => {},
	},
	{
		id: 'addProfilePicture',
		image: IMAGES.addProfilePicture,
		heading: <FormattedMessage {...i18n.addProfilePicture} />,
		description: <FormattedMessage {...i18n.addProfilePictureDescription} />,
		ctaText: <FormattedMessage {...i18n.addProfilePictureCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: () => {},
	},
];
